import { Pencil, Truck, User, Route as RouteIcon, Clock, PenLine } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import type { LogbookEntry } from '../../types/logbook';
import { useLogbookTransitions } from '../../hooks/useLogbookTransitions';
import { useLogbookAttachments } from '../../hooks/useLogbookAttachments';
import { LogbookTransitions } from './LogbookTransitions';
import { LogbookAttachmentsViewer } from './LogbookAttachmentsViewer';
import { colorDeTipo } from '../../utils/logbook';
import { formatDateTime } from '../../utils/trips';

/**
 * FE-13 — el detalle de una novedad, con sus cambios.
 *
 * Los cambios se piden aquí y no en el listado: la inmensa mayoría de las
 * novedades nunca se corrigen, y pedirlos por fila serían N+1 peticiones para
 * pintar una sola línea repetida.
 *
 * Los dos instantes se muestran SIEMPRE juntos y con nombres distintos —
 * "ocurrió" y "registrada"—, incluso cuando coinciden. Es la diferencia que
 * explica por qué una varada relatada hoy aparece en el expediente de
 * anteayer, y esconderla cuando son iguales enseñaría a leerla mal.
 */
export function LogbookEntryDetailModal({
  entry,
  onClose,
  onCorregir,
  puedeCorregir,
  ventanaVencida = false,
  puedeAdjuntar = false,
}: {
  entry: LogbookEntry | null;
  onClose: () => void;
  onCorregir?: (entry: LogbookEntry) => void;
  puedeCorregir: boolean;
  /** issue #150: el rol permite corregir, pero al Operador se le venció la ventana de 2 semanas. */
  ventanaVencida?: boolean;
  /** FE-30 — si el rol puede adjuntar fotos. Ver `puedeAdjuntar` en `utils/logbook`. */
  puedeAdjuntar?: boolean;
}) {
  const { transitions, isLoading, error } = useLogbookTransitions(entry?.id ?? null);
  const adjuntos = useLogbookAttachments(entry?.id ?? null);

  if (!entry) return null;

  const color = colorDeTipo(entry.entryTypeCode);

  return (
    <Modal
      isOpen={!!entry}
      onClose={onClose}
      title="Novedad"
      size="md"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cerrar</Button>
          {puedeCorregir && onCorregir && (
            ventanaVencida ? (
              <Button leftIcon={<Pencil size={14} />} disabled title="Pasaron más de 2 semanas desde el registro: ya no se puede corregir.">
                Corregir
              </Button>
            ) : (
              <Button leftIcon={<Pencil size={14} />} onClick={() => onCorregir(entry)}>
                Corregir
              </Button>
            )
          )}
        </>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        {puedeCorregir && ventanaVencida && (
          <div style={{ padding: '0.75rem 1rem', background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 'var(--radius-lg)', color: '#F59E0B', fontSize: '12.5px' }}>
            Pasaron más de 2 semanas desde el registro — esta novedad quedó en solo lectura.
          </div>
        )}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <span
            style={{
              alignSelf: 'flex-start', fontSize: '12px', fontWeight: 800, color,
              background: `${color}1F`, border: `1px solid ${color}59`,
              borderRadius: '99px', padding: '3px 11px',
            }}
          >
            {entry.entryTypeLabel}
          </span>

          <p style={{ margin: 0, fontSize: '14px', color: 'var(--text-primary)', lineHeight: 1.6 }}>
            {entry.description}
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.625rem' }}>
          <Dato icono={<Clock size={13} />} etiqueta="Ocurrió" valor={formatDateTime(entry.occurredAt)} />
          <Dato icono={<PenLine size={13} />} etiqueta="Registrada" valor={formatDateTime(entry.createdAt)} />
          {entry.vehiclePlate && <Dato icono={<Truck size={13} />} etiqueta="Vehículo" valor={entry.vehiclePlate} />}
          {entry.driverName && <Dato icono={<User size={13} />} etiqueta="Conductor" valor={entry.driverName} />}
          {entry.tripId && <Dato icono={<RouteIcon size={13} />} etiqueta="Viaje" valor="asignado explícitamente" />}
        </div>

        {/* FE-30. El botón de subir respeta la MISMA ventana que corregir:
            poner "adjuntar" justo debajo del aviso de "quedó en solo lectura"
            contradiría el aviso. Es UI, no seguridad — el backend no aplica la
            ventana todavía (#158) y por eso tampoco la aplica a los adjuntos:
            hacerlo solo aquí lo dejaría incoherente consigo mismo. */}
        <LogbookAttachmentsViewer
          attachments={adjuntos.attachments}
          isLoading={adjuntos.isLoading}
          error={adjuntos.error}
          subiendo={adjuntos.subiendo}
          progreso={adjuntos.progreso}
          urlDeDescarga={adjuntos.urlDeDescarga}
          quedanCupos={adjuntos.quedanCupos}
          onSubir={puedeAdjuntar && !ventanaVencida ? adjuntos.subir : undefined}
        />

        <LogbookTransitions transitions={transitions} isLoading={isLoading} error={error} />
      </div>
    </Modal>
  );
}

function Dato({ icono, etiqueta, valor }: { icono: React.ReactNode; etiqueta: string; valor: string }) {
  return (
    <div style={{ background: 'var(--bg-raised)', border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', padding: '0.5rem 0.75rem' }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', color: 'var(--text-muted)' }}>
        {icono} {etiqueta}
      </span>
      <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}>{valor}</span>
    </div>
  );
}
